import React from "react";
import { Link } from "react-router-dom";
import { Building2, Facebook, Instagram, Linkedin, Twitter, Youtube, MessageCircle, Mail, Phone, MapPin } from "lucide-react";

const SOCIAL = [
  { href: "https://facebook.com/ranaassociate", label: "Facebook", Icon: Facebook, testId: "social-facebook" },
  { href: "https://twitter.com/ranaassociate", label: "Twitter / X", Icon: Twitter, testId: "social-twitter" },
  { href: "https://linkedin.com/company/ranaassociate", label: "LinkedIn", Icon: Linkedin, testId: "social-linkedin" },
  { href: "https://instagram.com/ranaassociate", label: "Instagram", Icon: Instagram, testId: "social-instagram" },
  { href: "https://youtube.com/@ranaassociate", label: "YouTube", Icon: Youtube, testId: "social-youtube" },
  { href: "https://wa.me/919782519708", label: "WhatsApp", Icon: MessageCircle, testId: "social-whatsapp" },
];

const COLUMNS = [
  { title: "Product", links: [
    { to: "/", label: "Home", testId: "footer-home" },
    { to: "/login/bank", label: "Bank Sign in", testId: "footer-bank-login" },
    { to: "/login/field", label: "Field Executive Sign in", testId: "footer-field-login" },
    { to: "/signup/bank", label: "Register your Bank", testId: "footer-bank-signup" },
    { to: "/signup/field", label: "Join as Field Executive", testId: "footer-field-signup" },
    { to: "/training", label: "Training & Courses", testId: "footer-training" },
  ] },
  { title: "Company", links: [
    { to: "/about", label: "About us", testId: "footer-about" },
    { to: "/contact", label: "Contact", testId: "footer-contact" },
    { to: "/careers", label: "Careers", testId: "footer-careers" },
    { to: "/#roles", label: "Dashboards", testId: "footer-roles" },
    { to: "/#contact", label: "Bank partnerships", testId: "footer-partnerships" },
  ] },
  { title: "Legal", links: [
    { to: "/terms", label: "Terms & Conditions", testId: "footer-terms" },
    { to: "/privacy", label: "Privacy Policy", testId: "footer-privacy" },
    { to: "/refund", label: "Refund & Cancellation", testId: "footer-refund" },
    { to: "/disclaimer", label: "Valuation Disclaimer", testId: "footer-disclaimer" },
    { to: "/grievance", label: "Grievance Redressal", testId: "footer-grievance" },
  ] },
];

const BOTTOM_LINKS = [["/terms", "Terms"], ["/privacy", "Privacy"], ["/refund", "Refund"], ["/grievance", "Grievance"]];

function FooterBrand() {
  return (
    <div className="lg:pr-6">
      <Link to="/" className="flex items-center gap-2" data-testid="footer-brand">
        <div className="h-9 w-9 rounded-md bg-amber-500 text-slate-950 flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-white">Rana Associates</div>
          <div className="text-[11px] text-amber-300/80">Pan-Rajasthan · Property Valuation</div>
        </div>
      </Link>
      <p className="mt-5 text-sm text-slate-400 leading-relaxed">
        A trusted B2B valuation partner for banks &amp; NBFCs across all 33 districts of Rajasthan. Digitally audited. Territorially managed. Field-executed.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {SOCIAL.map(({ href, label, Icon, testId }) => (
          <a key={label} href={href} target="_blank" rel="noreferrer noopener" aria-label={label} data-testid={testId}
            className="h-9 w-9 rounded-full bg-slate-900 hover:bg-amber-500 hover:text-slate-950 border border-slate-800 flex items-center justify-center transition-colors">
            <Icon className="h-4 w-4" />
          </a>
        ))}
      </div>
    </div>
  );
}

function FooterLink({ to, children, testId }) {
  const cls = "text-sm text-slate-400 hover:text-white transition-colors";
  return <li>{to.includes("#") ? <a href={to} className={cls} data-testid={testId}>{children}</a> : <Link to={to} className={cls} data-testid={testId}>{children}</Link>}</li>;
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300 mb-4">{title}</div>
      <ul className="space-y-2.5">{links.map((l) => <FooterLink key={l.testId} to={l.to} testId={l.testId}>{l.label}</FooterLink>)}</ul>
    </div>
  );
}

function ContactItem({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
      <div><div className="text-slate-400">{label}</div>{children}</div>
    </div>
  );
}

function FooterContactStrip() {
  return (
    <div className="mt-12 pt-8 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
      <ContactItem icon={MapPin} label="Head office"><div className="text-slate-100" data-testid="footer-address">Beawar, Rajasthan · India 305901</div></ContactItem>
      <ContactItem icon={Phone} label="Sales & support">
        <a href="tel:+919782519708" className="text-slate-100 hover:text-amber-300" data-testid="footer-phone">+91 97825 19708</a>
        <span className="text-slate-500"> · </span>
        <a href="tel:+919166363761" className="text-slate-100 hover:text-amber-300" data-testid="footer-phone-2">+91 91663 63761</a>
      </ContactItem>
      <ContactItem icon={Mail} label="Email"><a href="mailto:support@ranaassociate.com" className="text-slate-100 hover:text-amber-300" data-testid="footer-email">support@ranaassociate.com</a></ContactItem>
    </div>
  );
}

function FooterBottomBar() {
  return (
    <div className="border-t border-slate-800 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div>© {new Date().getFullYear()} Rana Associates. All rights reserved. Pan-Rajasthan Property Valuation Operations.</div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 justify-center">
          {BOTTOM_LINKS.map(([to, label]) => <Link key={to} to={to} className="hover:text-slate-300">{label}</Link>)}
        </div>
      </div>
    </div>
  );
}

export default function SiteFooter() {
  return (
    <footer className="bg-slate-950 text-slate-300 border-t border-slate-800" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <FooterBrand />
          {COLUMNS.map((c) => <FooterCol key={c.title} title={c.title} links={c.links} />)}
        </div>
        <FooterContactStrip />
      </div>
      <FooterBottomBar />
    </footer>
  );
}
